import {referenceMarkup, referenceStyles} from './referenceContent';
import {runReference} from './referenceRuntime';
import {nativeStyles} from './nativeStyles';

export const STORAGE_KEYS = {
  'torlopov-local-members':'members', 'torlopov-client-profiles':'profiles',
  'torlopov-excluded':'excluded', 'torlopov-monthly-plans':'monthly_plans',
  'torlopov-plan-history-facts':'plan_history_facts', 'torlopov-work-notes':'work_notes',
  'torlopov-balance-history':'balance_history', 'torlopov-closure-history':'closure_history',
  'torlopov-standing-edits-v1':'standing_edits',
};
const READONLY_HISTORY = new Set(['balance_history','closure_history','plan_history_facts']);
const EDIT_CONTROLS = '[data-profile],[data-work-note],[data-standing-edit],[data-history-month],#editPlan,#historyEditPlan,#addMember,#manage,#standingAdd,#standingRestore,#standingUndo,#standingDeleteConfirm,#standingImportConfirm,'+
  '#planForm input,#planForm button[type=submit],#profileForm input,#profileForm select,#profileForm textarea,#profileForm button[type=submit],'+
  '#workNoteForm input,#workNoteForm textarea,#workNoteForm button[type=submit],#standingEditorForm input,#standingEditorForm select,#standingEditorForm button';

// Native DOM in a scoped shadow root: no second document, frame, global scripts,
// access token, API-generated executable HTML or document-level scroll area.
export function mountManagerDashboard(host, {snapshot, settings, editable, groupTag, onSetting, onRefresh, onImportRequested}) {
  const payloadTag=typeof snapshot.scope==='object'?snapshot.scope?.tag:snapshot.scope;
  const tag=groupTag||payloadTag;
  if(!['торлопов','швец','малыгина','плашкевич'].includes(tag)||payloadTag!==tag)throw new Error('Группа снимка не совпадает с назначенной панелью');
  const root = host.shadowRoot || host.attachShadow({mode:'open'});
  root.replaceChildren();
  const owner = host.ownerDocument;
  const style = owner.createElement('style');
  style.textContent = referenceStyles + '\n' + nativeStyles;
  const body = owner.createElement('div');
  body.className = 'manager-document';
  body.innerHTML = referenceMarkup; // Static reviewed markup; source data is never interpolated here.
  root.append(style, body);
  const timers = new Set();
  let disposed = false, canEdit = editable;
  const later = (fn, delay) => {
    const id = setTimeout(() => {timers.delete(id); if (!disposed) fn();}, delay);
    timers.add(id); return id;
  };
  const storage = Object.fromEntries(Object.entries(STORAGE_KEYS).filter(([,ns]) => settings[ns])
    .map(([key,ns]) => [key, JSON.stringify(settings[ns].payload)]));
  const scopedWindow = {
    DASHBOARD_DATA: structuredCloneSafe(snapshot), EMBEDDED_SNAPSHOT:true,
    panelScope:{tag},
    location:{reload:onRefresh},
    portalStorage:{
      getItem:key => storage[key] ?? null,
      setItem:(key,value) => {
        const namespace = STORAGE_KEYS[key];
        if (!namespace) throw new Error('Unknown setting');
        if (storage[key] === value) return;
        if (!canEdit) {
          if (READONLY_HISTORY.has(namespace)) return;
          throw new Error('Изменение панели не разрешено');
        }
        const payload = JSON.parse(value);
        storage[key] = value;
        if (!disposed) onSetting(namespace, payload);
      },
      removeItem:() => {throw new Error('Удаление настройки не поддерживается');},
    },
    portalSend:type => {if (!disposed) {if (type==='refresh') onRefresh(); else if(type==='import_requested') onImportRequested();}},
  };
  const scopedDocument = {
    body, head:root,
    getElementById:id => root.getElementById(id),
    querySelectorAll:selector => root.querySelectorAll(selector),
    createElement:name => {
      if (name.toLowerCase()==='script') throw new Error('Внешние скрипты панели запрещены');
      return owner.createElement(name);
    },
    addEventListener:(...args) => body.addEventListener(...args),
    get activeElement() {return root.activeElement;},
  };
  const aside = body.querySelector('aside');
  const navigation = owner.createElement('nav');
  navigation.className = 'manager-sections';
  navigation.setAttribute('role','tablist');
  navigation.setAttribute('aria-label','Разделы персональной панели');
  const labels = {overview:'Обзор',daily:'Продажи',clients:'Клиенты',tops:'Товары',profiles:'Описания',
    plans:'Планы',activity:'Задачи',closure:'Долги до 10-го',debt:'Задолженность',
    returns:'Возвраты и списания',standings:'Стендинги',method:'Методика'};
  for (const button of aside.querySelectorAll('button[data-view]')) {
    button.textContent = labels[button.dataset.view];
    button.id = 'manager-tab-' + button.dataset.view;
    button.setAttribute('role','tab');
    button.setAttribute('aria-controls',button.dataset.view);
    navigation.append(button);
  }
  aside.remove();
  const main = body.querySelector('main');
  main.className = 'manager-content';
  const oldHeader = main.querySelector('header');
  const actions = oldHeader.querySelector('.actions');
  actions.className = 'manager-member-actions';
  actions.querySelector('#refresh').hidden = true;
  const tools = owner.createElement('details');
  tools.className='manager-panel-tools';
  const toolsLabel=owner.createElement('summary');toolsLabel.textContent='Параметры панели';
  const toolsBody=owner.createElement('div');toolsBody.className='manager-panel-tools-body';
  tools.append(toolsLabel,toolsBody);toolsBody.append(actions);
  const freshness=main.querySelector('.freshness-panel');
  if(freshness)toolsBody.append(freshness);
  main.prepend(tools);
  // Retain the referenced timestamp node for the unchanged renderer, invisibly.
  const updated = oldHeader.querySelector('#updated'); updated.hidden = true;
  main.prepend(updated); oldHeader.remove();
  main.prepend(navigation);
  const status = main.querySelector('#status'); status.classList.add('manager-reference-status');
  const explanatory = status.nextElementSibling;
  if (explanatory?.tagName==='P') explanatory.hidden = true;
  for (const view of main.querySelectorAll('.view')) {
    view.setAttribute('role','tabpanel'); view.setAttribute('aria-labelledby','manager-tab-'+view.id);
  }
  // Keep the original chronological comparison and all event/calculation code.
  let result;
  try {result = runReference(scopedWindow, scopedDocument, later);}
  catch (error) {timers.forEach(clearTimeout);root.replaceChildren();throw error;}
  for(const label of root.querySelectorAll('[data-panel-group]'))label.textContent=tag;
  const syncTabs = () => {
    for (const button of navigation.querySelectorAll('[role=tab]')) {
      const active = !root.getElementById(button.dataset.view).hidden;
      button.setAttribute('aria-selected',String(active));button.tabIndex=active?0:-1;
    }
  };
  body.addEventListener('click',syncTabs);
  navigation.addEventListener('keydown',event => {
    const buttons = [...navigation.querySelectorAll('[role=tab]')];
    const index = buttons.indexOf(root.activeElement);
    if (index<0 || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
    event.preventDefault();
    const next = event.key==='Home'?0:event.key==='End'?buttons.length-1:
      (index+(event.key==='ArrowRight'?1:-1)+buttons.length)%buttons.length;
    buttons[next].click();buttons[next].focus();
  });
  const enforceReadOnly = () => {
    if (!canEdit) for (const el of root.querySelectorAll(EDIT_CONTROLS)) {
      el.disabled=true;el.title='Только просмотр: изменение не разрешено';
    }
  };
  body.addEventListener('submit',event => {if (!canEdit) {event.preventDefault();event.stopImmediatePropagation();}},true);
  const observer = new MutationObserver(enforceReadOnly);
  observer.observe(body,{childList:true,subtree:true});
  enforceReadOnly();syncTabs();
  return {...result, root, setReadOnly:() => {canEdit=false;enforceReadOnly();},
    dispose:() => {disposed=true;observer.disconnect();timers.forEach(clearTimeout);
      for(const dialog of root.querySelectorAll('dialog[open]')) dialog.close();
      root.replaceChildren();},
  };
}

function structuredCloneSafe(value) {return JSON.parse(JSON.stringify(value));}
